import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { SEOManager } from "./seoManager";
import { setupPage } from "./seoGenerator";
import {
  WebsiteData,
  PropertyData,
  ArticleData,
  BreadcrumbsData,
} from "./structuredData";
import { SEOData } from "./seoData";

const keywords = `
cazare, proprietate de închiriat, apartament de vacanță, vilă România, hotel boutique, cazare pe litoral, pensiune, sejur, rezervare online, locuri de cazare
`.trim();

export default function Seo({ pageType = "home", data = null }) {
  const [meta, setMeta] = useState({});
  const [og, setOg] = useState({});
  const [twitter, setTwitter] = useState({});
  const [structuredData, setStructuredData] = useState([]);

  useEffect(() => {
    let metaObj = {};
    let ogObj = {};
    let twitterObj = {};
    let sd = [];

    switch (pageType) {
      case "home":
        metaObj = SEOData.generateMeta({ pageTitle: `${SEOData.siteTitle} | Proprietati Premium` });
        ogObj = SEOData.generateOG({ pageTitle: `${SEOData.siteTitle} | Proprietati Premium` });
        twitterObj = SEOData.generateTwitter({ pageTitle: `${SEOData.siteTitle} | Proprietati Premium` });
        sd.push(new WebsiteData().toJson());
        break;

      case "proprietate":
        if (data) {
          sd.push(
            new PropertyData({
              name: data.name,
              description: data.description || SEOData.siteDescription,
              url: `${SEOData.siteUrl}/proprietate/${data.id}`,
            }).toJson()
          );

          metaObj = SEOData.generateMeta({
            pageTitle: data.name,
            keywords: keywords,
            description: `Rezerva acum ${data.name}, o proprietate premium în ${data.location?.city || ''}, Romania. Camere confortabile, facilităti moderne si optiuni de masa flexibile. Disponibil pentru sejururi scurte si lungi.`
          });
          ogObj = SEOData.generateOG({ pageTitle: data.name });
          twitterObj = SEOData.generateTwitter({ pageTitle: data.name });

          // Breadcrumbs
          const crumbs = [
            { name: "Acasa", url: "/" },
            { name: "Proprietati", url: "/proprietati" },
            { name: data.name, url: `/proprietate/${data.id}` },
          ];
          sd.push(new BreadcrumbsData(crumbs).toJson());
        }
        break;

      case "blog":
        if (data) {
          sd.push(
            new ArticleData({
              headline: data.title,
              author: SEOData.siteAuthor,
              datePublished: data.lastModified,
              dateModified: data.lastModified,
              image: SEOData.ogImage,
              url: `${SEOData.siteUrl}/blog/${data.slug}`,
            }).toJson()
          );

          metaObj = SEOData.generateMeta({ pageTitle: data.title });
          ogObj = SEOData.generateOG({ pageTitle: data.title });
          twitterObj = SEOData.generateTwitter({ pageTitle: data.title });

          const crumbs = [
            { name: "Acasa", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: data.title, url: `/blog/${data.slug}` },
          ];
          sd.push(new BreadcrumbsData(crumbs).toJson());
        }
        break;

      default:
        metaObj = SEOData.generateMeta({ pageTitle: SEOData.siteTitle });
        ogObj = SEOData.generateOG({ pageTitle: SEOData.siteTitle });
        twitterObj = SEOData.generateTwitter({ pageTitle: SEOData.siteTitle });
        sd.push(new WebsiteData().toJson());
    }

    setMeta(metaObj);
    setOg(ogObj);
    setTwitter(twitterObj);
    setStructuredData(sd);

    // Aplica in DOM prin SEOManager
    SEOManager.setMeta({
      title: metaObj.title || SEOData.siteTitle,
      description: metaObj.description,
      keywords: metaObj.keywords,
      author: metaObj.author,
      canonicalUrl: metaObj.canonical,
    });

    SEOManager.setOpenGraph({
      title: ogObj["og:title"] || metaObj.title,
      description: ogObj["og:description"] || metaObj.description,
      image: ogObj["og:image"],
      url: ogObj["og:url"],
      updatedTime: ogObj["og:updated_time"],
    });

    SEOManager.setTwitterCard({
      title: twitterObj["twitter:title"] || metaObj.title,
      description: twitterObj["twitter:description"] || metaObj.description,
      image: twitterObj["twitter:image"] || ogObj["og:image"],
      cardType: twitterObj["twitter:card"],
      siteHandle: twitterObj["twitter:site"],
      creatorHandle: twitterObj["twitter:creator"],
    });

    sd.forEach(item => SEOManager.setStructuredData(item));

    // SetupPage pentru eventuale efecte suplimentare
    setupPage({ meta: metaObj, og: ogObj, twitter: twitterObj, structuredData: sd });
  }, [pageType, data]);

  return (
    <Helmet>
      {meta.title && <title>{meta.title}</title>}
      {Object.entries(meta).map(([key, value]) => (
        <meta key={key} name={key} content={value} />
      ))}
      {Object.entries(og).map(([key, value]) => (
        <meta key={key} property={key} content={value} />
      ))}
      {Object.entries(twitter).map(([key, value]) => (
        <meta key={key} name={key} content={value} />
      ))}
      {structuredData.map((sdItem, idx) => (
        <script
          key={idx}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(sdItem) }}
        />
      ))}
    </Helmet>
  );
}
